import React from "react"
import { Footer } from "./Footer"
import { Grid, Select, Dropdown } from "semantic-ui-react"
import { FormContainer, Worksheet, LabeledInput, CheckboxYesNo } from "uikit"
import { AddRealEstateModal } from "../../AddRealEstateModal"
import ConnectedAccountBar from "../../ConnectedAccountBar"
import HumanTimeSince from "components/HumanTimeSince"
import { useStore } from "store"

const MortgageAccountForm = ({
  data: {
    fields: {
      balance,
      interest_rate,
      origination_year,
      origination_month,
      term_years,
      termYearChoice,
      property_id,
      additional_payment
    },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  submitHeader = "",
  isNew,
  onDelete,
  addRealEstateProperty,
  propertyOptions,
  monthOptions,
  termYearChoices,
  lastUpdatedAt,
  account,
  isPrimaryResidenceDefaultValue
}) => {
  const addModalOptions = {
    values: { is_primary_residence: isPrimaryResidenceDefaultValue }
  }
  let currentYear = new Date().getFullYear()
  let yearOptions = [...Array(31).keys()].map(key => {
    let year = currentYear - key + ""
    return { text: year, value: year }
  })
  return (
    <Grid>
      {account.connection_id ? (
        <Grid.Row style={{ borderBottom: "1px solid #ccc" }}>
          <Grid.Column>
            <ConnectedAccountBar {...account} />
          </Grid.Column>
        </Grid.Row>
      ) : null}
      <Grid.Row>
        <Grid.Column>
          <Worksheet error={error} style={{ borderTop: "1px solid #ccc" }}>
            <Worksheet.Body>
              <Worksheet.Row
                label="What is the current balance of your mortgage?"
                description={
                  account.connection_id
                    ? "Automatically updated through the account connection"
                    : null
                }
                paddedField
              >
                <Worksheet.Field error={balance.error}>
                  <LabeledInput
                    {...balance}
                    label=""
                    iconText="$"
                    iconPosition="left"
                    placeholder="0"
                    disabled={!!account.connection_id}
                    data-testid="balance"
                  />
                  {lastUpdatedAt}
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="What is your mortgage interest rate?"
                paddedField
              >
                <Worksheet.Field error={interest_rate.error} padded>
                  <LabeledInput
                    {...interest_rate}
                    label=""
                    iconText="%"
                    placeholder="0"
                    data-testid="interest_rate"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="When did you originate your mortgage?"
                paddedField
              >
                <Worksheet.Field
                  error={origination_year.error || origination_month.error}
                >
                  <Grid>
                    <Grid.Row>
                      <Grid.Column width={7}>
                        <Select
                          {...origination_year}
                          fluid
                          placeholder="year"
                          options={yearOptions}
                          data-testid="origination_year"
                        />
                      </Grid.Column>
                      <Grid.Column width={9}>
                        <Select
                          {...origination_month}
                          fluid
                          placeholder="month"
                          options={monthOptions}
                          data-testid="origination_month"
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="What was the original term on the mortgage (in years)?"
                paddedField
              >
                <Worksheet.Field error={term_years.error} padded>
                  <Select
                    {...termYearChoice}
                    onChange={(event, data) => {
                      if (data.value !== "Other") {
                        term_years.onChange(event, data)
                      } else {
                        term_years.onChange(event, "")
                      }
                      termYearChoice.onChange(event, data)
                    }}
                    fluid
                    placeholder="years"
                    options={termYearChoices}
                    data-testid="term_years"
                  />
                </Worksheet.Field>
                {termYearChoice.value === "Other" ? (
                  <Worksheet.Row label="Please enter term years" paddedField>
                    <Worksheet.Field>
                      <input
                        {...term_years}
                        autoComplete="off"
                        placeholder="years"
                      />
                    </Worksheet.Field>
                  </Worksheet.Row>
                ) : null}
              </Worksheet.Row>
              <Worksheet.Row
                label="What property is financed with this mortgage?"
                paddedField
              >
                <Worksheet.Field error={property_id.error}>
                  <Dropdown
                    {...property_id}
                    placeholder="Real Estate"
                    options={[
                      ...propertyOptions,
                      { text: "Add Property...", value: "__ADD__" }
                    ]}
                    className="has-add-option"
                    selection
                    data-testid="property"
                  />
                  {property_id.value === "__ADD__" && (
                    <AddRealEstateModal
                      submit={propertyValues =>
                        addRealEstateProperty(
                          propertyValues
                        ).then(({ propertyId }) =>
                          property_id.onChange(null, { value: propertyId })
                        )
                      }
                      onCancel={() =>
                        property_id.onChange(null, { value: null })
                      }
                      {...addModalOptions}
                    />
                  )}
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="Do you make additional payments above and beyond the required monthly payment?"
                paddedField
              >
                <Worksheet.Field>
                  <CheckboxYesNo
                    fluid
                    checked={additional_payment.value !== ""}
                    toggle={() => {
                      if (additional_payment.value !== "") {
                        additional_payment.onChange(null, { value: "" })
                      } else {
                        additional_payment.onChange(null, { value: 0 })
                      }
                    }}
                    name="additionalPaymentToggle"
                  />
                </Worksheet.Field>
                {additional_payment.value !== "" ? (
                  <Worksheet.Row
                    paddedField
                    label="How much additional payment do you make each month?"
                  >
                    <Worksheet.Field padded>
                      <LabeledInput
                        {...additional_payment}
                        label=""
                        iconText="$"
                        iconPosition="left"
                        placeholder="0"
                      />
                    </Worksheet.Field>
                  </Worksheet.Row>
                ) : null}
              </Worksheet.Row>
            </Worksheet.Body>
            <Footer
              isNew={isNew}
              submitLabel={submitLabel}
              onSubmit={onSubmit}
              onDelete={onDelete}
              isModified={isModified}
              deleteLabel={account.connection_id ? "Ignore Account" : "Delete"}
              hasConnectionId={account.connection_id ? true : false}
              error={error}
            />
          </Worksheet>{" "}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

const MortgageAccountFormContainer = FormContainer(
  {
    balance: {
      type: "unit",
      required: true,
      negative: true
    },
    interest_rate: {
      type: "decimal",
      minValue: 0,
      maxValue: 100
    },
    origination_year: {
      type: "string"
    },
    origination_month: {
      type: "string"
    },
    term_years: {
      type: "unit"
    },
    termYearChoice: {
      type: "string",
      required: false,
      submit: false
    },
    property_id: {
      type: "string"
    },
    additional_payment: {
      type: "unit",
      required: false
    }
  },
  (
    { fields: { origination_month, origination_year, term_years } },
    ...form
  ) => {
    const currentMonth = new Date().getMonth() + 1
    const currentYear = new Date().getFullYear()
    const originationYear = Number(origination_year.value)
    const originationMonth = Number(origination_month.value)
    const termYears = Number(term_years.value)
    if (originationYear + termYears < currentYear) {
      return {
        fields: {
          term_years: `Please check origination date and term as it looks you've already paid this loan off.`
        }
      }
    }
    if (
      termYears &&
      originationYear + termYears === currentYear &&
      originationMonth < currentMonth
    ) {
      return {
        fields: {
          origination_year: `Please check origination date and term as it looks you've already paid this loan off.`
        }
      }
    }
    return {
      fields: {
        origination_year: null,
        term_years: null
      }
    }
  }
)(MortgageAccountForm)

const MortgageAccount = ({ household, account, ...formProps }) => {
  const isNew = account === null
  let values = isNew ? {} : account
  const lastUpdatedAt = !isNew ? (
    <p
      style={{
        fontSize: "0.8em",
        textAlign: "right",
        fontWeight: "normal"
      }}
    >
      Updated <HumanTimeSince time={values.balance_updated_at} /> ago
    </p>
  ) : null

  const [properties, { addProperty }] = useStore(
    "householdProperties",
    household.id
  )
  const allProperties = Object.values(properties)
  const propertyOptions = Object.values(properties)
    .filter(property => property.type === "realestate")
    .map(({ description: text, id: value }) => ({
      text,
      value
    }))
  const primaryResidence = allProperties.find(
    property => property.is_primary_residence
  )
  const isPrimaryResidenceDefaultValue = !primaryResidence
  const monthOptions = [
    { value: "1", text: "January" },
    { value: "2", text: "February" },
    { value: "3", text: "March" },
    { value: "4", text: "April" },
    { value: "5", text: "May" },
    { value: "6", text: "June" },
    { value: "7", text: "July" },
    { value: "8", text: "August" },
    { value: "9", text: "September" },
    { value: "10", text: "October" },
    { value: "11", text: "November" },
    { value: "12", text: "December" }
  ]

  const termYearChoices = ["5", "10", "15", "20", "30", "Other"].map(n => ({
    text: n,
    value: n
  }))
  if (!isNew) {
    const termyearChosen = termYearChoices.find(
      option => parseInt(option.value, 10) === values.term_years
    )
    let termYearChoice = termyearChosen ? termyearChosen.value : "Other"
    values = Object.assign({}, account, { termYearChoice })
  }
  // TODO Create Action
  const addRealEstateProperty = propertyValues =>
    addProperty({
      household_id: household.id,
      type: "realestate",
      ...propertyValues
    })
  return (
    <MortgageAccountFormContainer
      {...formProps}
      values={values}
      lastUpdatedAt={lastUpdatedAt}
      account={values}
      propertyOptions={propertyOptions}
      monthOptions={monthOptions}
      termYearChoices={termYearChoices}
      addRealEstateProperty={addRealEstateProperty}
      isPrimaryResidenceDefaultValue={isPrimaryResidenceDefaultValue}
    />
  )
}

export default MortgageAccount
